import React from "react";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { JcCommonProps, JcKeyFunction, JcSize, JcUtils } from "../utils/JcUtils";
import { JcBox, JcRow } from "./JcBox";
import { JcButtonType, jcButtonAppearanceClasses } from "./button/JcButton";
import { JcIconButton } from "./button/JcIconButton";
import { JcBuiltInIcon } from "./icon/JcBuiltInIcons";
import { JcIcon, JcIconType } from "./icon/JcIcon";

export interface JcSortableListProps<T> extends JcCommonProps {
    readonly items: Readonly<T[]>;
    /** `movedId` is whichever item the user actually grabbed/clicked — for an
     *  adjacent single-slot move, the resulting array alone can't tell that
     *  apart from its neighbor having moved the other way, so callers that
     *  care which one moved (e.g. re-timing a reordered event log) need it
     *  reported explicitly rather than inferred after the fact. */
    readonly onReorder: (items: T[], movedId: string) => void;
    readonly itemRenderer: (item: T) => React.ReactNode;
    readonly keyFunction: JcKeyFunction<T>;
    /** Accessible name for the list, e.g. "Team roster order". */
    readonly ariaLabel: string;
    /** Whether reordering is offered at all — `false` renders a plain list
     *  with no drag handle or move buttons (e.g. a read-only viewer without
     *  permission to reorder). Defaults to `true`. */
    readonly sortable?: boolean;
    /** Temporarily inert (e.g. mid-save) while the controls still show —
     *  unlike `sortable`, which removes them entirely. */
    readonly disabled?: boolean;
    /** Marks an item static: not draggable, not a drop target, and no
     *  handle/move buttons of its own — but still displaced by its neighbors'
     *  moves (e.g. a section divider row). */
    readonly isItemDisabled?: (item: T) => boolean;
}

/**
 * A reorderable list — a drag handle (pointer or keyboard, via dnd-kit) plus
 * explicit move-up/move-down buttons on every row, so reordering never
 * requires discovering a drag gesture. `itemRenderer`'s content stays
 * independently interactive: only the handle carries the drag listeners.
 */
export const JcSortableList = <T,>(props: JcSortableListProps<T>): React.ReactElement => {
    const { items, onReorder, itemRenderer, keyFunction, ariaLabel, sortable = true, disabled = false, isItemDisabled } = props;
    const commonProps = JcUtils.commonProps(props, ["jc-sortable-list"]);
    const ids = items.map(keyFunction);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    if (!sortable) {
        return (
            <ul {...commonProps} aria-label={ariaLabel}>
                {items.map(item => (
                    <li key={keyFunction(item)} className="jc-sortable-list-item">
                        {itemRenderer(item)}
                    </li>
                ))}
            </ul>
        );
    }

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (over == null || active.id === over.id) {
            return;
        }
        const from = ids.indexOf(active.id as string);
        const to = ids.indexOf(over.id as string);
        onReorder(arrayMove([...items], from, to), active.id as string);
    };

    const move = (index: number, direction: -1 | 1): void => {
        const target = index + direction;
        if (target < 0 || target >= items.length) {
            return;
        }
        const movedId = keyFunction(items[index]);
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        onReorder(next, movedId);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
                <ul {...commonProps} aria-label={ariaLabel}>
                    {items.map((item, index) => (
                        <JcSortableListItem
                            key={keyFunction(item)}
                            id={keyFunction(item)}
                            disabled={disabled}
                            itemDisabled={isItemDisabled?.(item) ?? false}
                            isFirst={index === 0}
                            isLast={index === items.length - 1}
                            onMoveUp={() => move(index, -1)}
                            onMoveDown={() => move(index, 1)}
                        >
                            {itemRenderer(item)}
                        </JcSortableListItem>
                    ))}
                </ul>
            </SortableContext>
        </DndContext>
    );
};

interface JcSortableListItemProps {
    readonly id: string;
    readonly disabled: boolean;
    readonly itemDisabled: boolean;
    readonly isFirst: boolean;
    readonly isLast: boolean;
    readonly onMoveUp: () => void;
    readonly onMoveDown: () => void;
    readonly children: React.ReactNode;
}

const JcSortableListItem = (props: JcSortableListItemProps): React.ReactElement => {
    const { id, disabled, itemDisabled, isFirst, isLast, onMoveUp, onMoveDown, children } = props;
    const {
        setNodeRef,
        setActivatorNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: disabled || itemDisabled });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    if (itemDisabled) {
        // Static row: no controls, but keeps its ref/transform so it still
        // displaces while neighbors drag past it.
        return (
            <li ref={setNodeRef} style={style} className="jc-sortable-list-item jc-sortable-list-item-static">
                {children}
            </li>
        );
    }

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={JcUtils.classNameString(["jc-sortable-list-item", isDragging && "jc-sortable-list-item-dragging"])}
        >
            <JcRow className="jc-sortable-list-row" alignCenter>
                {/* A hand-rolled <button>, like JcList's selectable row — dnd-kit's
                    `attributes`/`listeners` (tabIndex, onPointerDown, onKeyDown, …)
                    need to land on the real DOM node; JcButton's curated prop
                    surface has no passthrough for them. */}
                <button
                    ref={setActivatorNodeRef}
                    type="button"
                    className={JcUtils.classNameString([
                        ...jcButtonAppearanceClasses({ buttonType: JcButtonType.GHOST, size: JcSize.SMALL, disabled }, !disabled),
                        "jc-icon-button",
                        "jc-icon-button-size-small",
                        "jc-sortable-list-handle",
                    ])}
                    aria-label="Drag to reorder"
                    disabled={disabled}
                    {...attributes}
                    {...listeners}
                >
                    <JcIcon type={JcIconType.BUILT_IN} icon={JcBuiltInIcon.BARS_3} size={JcSize.NONE} />
                </button>
                <JcBox flexGrow>{children}</JcBox>
                <JcIconButton
                    className="jc-sortable-list-move"
                    iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.ARROW_UP }}
                    ariaLabel="Move up"
                    disabled={disabled || isFirst}
                    onClick={onMoveUp}
                />
                <JcIconButton
                    className="jc-sortable-list-move"
                    iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.ARROW_DOWN }}
                    ariaLabel="Move down"
                    disabled={disabled || isLast}
                    onClick={onMoveDown}
                />
            </JcRow>
        </li>
    );
};
